import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import ParticleField from "../ParticleField";
import { LogoMark } from "../Logo";
import { SplitLines } from "../motion";

const MAP_BG = "https://static.prod-images.emergentagent.com/jobs/60fe7cfd-579c-48af-b4a9-bdfd3f1b809e/images/914819600daccdf0e6c8cf83f0f6de0f86a9c6712da306b52d3ca9786b152dcf.jpeg";

export default function Hero() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const ref = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const bgX = useTransform(sx, [-1, 1], [18, -18]);
  const bgY = useTransform(sy, [-1, 1], [12, -12]);
  const rotY = useTransform(sx, [-1, 1], [-10, 10]);
  const rotX = useTransform(sy, [-1, 1], [8, -8]);

  const { scrollY } = useScroll();
  const scrollBg = useTransform(scrollY, [0, 600], [0, 90]);
  const scrollFade = useTransform(scrollY, [0, 500], [1, 0.15]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  const titleLines = isAr
    ? [t("home.hero_title")]
    : ["Genetics", <>{"\u200b"}<span className="text-gold">neXt</span> Researchers</>];

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative min-h-[calc(100vh-76px)] flex flex-col overflow-hidden"
      data-testid="home-hero"
    >
      <motion.div style={{ x: bgX, y: bgY }} className="absolute -inset-8 z-0">
        <motion.div
          style={{ backgroundImage: `url(${MAP_BG})`, y: scrollBg, opacity: scrollFade }}
          className="absolute inset-0 bg-cover bg-center mix-blend-multiply opacity-90"
        />
      </motion.div>
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-ivory/30 via-transparent to-ivory" />
      <ParticleField count={60} className="z-10" />

      <div className="relative z-20 flex-1 flex flex-col items-center justify-center text-center px-6 pt-10 pb-28 lg:pb-36">
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 900 }}
          className="drift-slow"
        >
          <LogoMark large className="h-32 sm:h-40 lg:h-52 w-auto drop-shadow-[0_18px_30px_rgba(184,148,79,0.25)]" />
        </motion.div>

        <h1 className="font-display text-[44px] sm:text-6xl lg:text-[84px] leading-[0.98] mt-6 lg:mt-8 text-forest max-w-5xl">
          <SplitLines lines={titleLines} delay={0.35} stagger={0.14} />
        </h1>

        <div className="mt-6 lg:mt-7 text-lg lg:text-2xl text-ink/80 max-w-3xl">
          <SplitLines lines={[t("home.hero_sub")]} delay={0.7} />
        </div>
        <div className="mt-4 text-[11px] lg:text-xs uppercase tracking-[0.28em] font-semibold text-gold max-w-2xl">
          <SplitLines lines={[t("home.hero_caps")]} delay={0.85} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-4"
        >
          <Link to="/join" data-testid="home-cta-join" className="btn-forest">
            {t("home.cta_join")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
          <Link to="/programs" data-testid="home-cta-programs" className="btn-gold-outline">
            {t("home.cta_explore")}
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1 }}
          className="mt-8 text-gold/70 animate-bounce"
          aria-hidden="true"
        >
          <ChevronDown className="h-5 w-5" />
        </motion.div>
      </div>

    </section>
  );
}
